import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Spinner,
  Alert,
  Button,
  Form,
  InputGroup,
  Row,
  Col,
  Modal,
  Badge,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faSearch,
  faTimes,
  faFilter,
  faPlay,
  faSyncAlt,
  faStop,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';
import { hasEditPermission } from '../../utils/authConfig';
import {
  STATUSES,
  errorCode,
  describeError,
  isNotDeployed,
  fetchAppleTransferSummary,
  fetchAppleTransferUsers,
  transferAppleUser,
  previewAppleTransferBatch,
  runAppleTransferBatch,
  fetchAppleTransferAction,
  cancelAppleTransferAction,
  fetchAppleTransferActions,
  fakeAppleTransferSummary,
  fakeAppleTransferUsers,
  fakeAppleTransferPreview,
  fakeAppleTransferActions,
} from '../../services/appleTransferApi';
import AppleTransferUserDetail from './AppleTransferUserDetail';
import { STATUS_VARIANTS, NEXT_ACTION_LABELS, formatDate, shortSub } from './transferFormat';
import './appleTransfer.scss';

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 500;
const POLL_MS = 4000;

// The "Run now" buttons. Without an action the server picks the phase default: generate before
// the transfer, exchange (step 2) after it.
const RUN_BUTTONS = [
  { key: 'errors', scope: 'errors', label: 'users with errors', variant: 'danger' },
  { key: 'pending', scope: 'pending', label: 'users still pending', variant: 'primary' },
];

// OC-729: after the transfer, step 1 for VI users that still have no transfer_sub. The VI key can
// still generate it then; scope "remaining" only takes VI users without a transfer_sub
// (pending or failed before), so users that already have one are never touched.
const GENERATE_BUTTON = {
  key: 'generate',
  scope: 'remaining',
  action: 'generate',
  label: 'generate transfer_sub',
  description: 'VI users without a transfer_sub',
  variant: 'outline-primary',
};

export const runButtonsFor = (phase) => (phase === 'after_transfer' ? [...RUN_BUTTONS, GENERATE_BUTTON] : RUN_BUTTONS);

const runDescription = (run) => run?.description ?? run?.label;

const isActive = (action) => action && ['queued', 'running'].includes(action.status);

const AppleTransfer = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [fake, setFake] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('');
  const [team, setTeam] = useState('');
  const [hasSub, setHasSub] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS).trim();

  const [actions, setActions] = useState([]);
  const [runningAction, setRunningAction] = useState(null);

  // Confirm dialog: either one user, or a batch scope with its previewed total.
  const [confirmUser, setConfirmUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [confirmRun, setConfirmRun] = useState(null); // { ...button, action, total, loading, changed }
  const [confirmError, setConfirmError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const canEdit = hasEditPermission();
  const counts = summary?.counts ?? {};

  const buildParams = () => {
    const params = { page: currentPage, items_per_page: PER_PAGE };
    if (debouncedSearch) params.q = debouncedSearch;
    if (status) params.status = status;
    if (team) params.team = team;
    if (hasSub) params.has_transfer_sub = hasSub;
    if (onlyErrors) params.has_error = 1;
    return params;
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    const params = buildParams();
    try {
      const res = await fetchAppleTransferUsers(params);
      const data = res.data?.success ? res.data.data : null;
      setFake(false);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalItems(data?.objects?.total ?? 0);
    } catch (err) {
      if (isNotDeployed(err)) {
        const data = fakeAppleTransferUsers(params);
        setFake(true);
        setItems(data.items);
        setTotalItems(data.objects.total);
      } else {
        setError(describeError(err, 'Failed to fetch Apple users'));
        setItems([]);
        setTotalItems(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetchAppleTransferSummary();
      const data = res.data?.data ?? null;
      setSummary(data);
      setRunningAction(data?.running_action ?? null);
    } catch (err) {
      if (isNotDeployed(err)) {
        setSummary(fakeAppleTransferSummary());
      } else {
        setError(describeError(err, 'Failed to fetch the summary'));
      }
    }
  };

  const fetchActions = async () => {
    try {
      const res = await fetchAppleTransferActions({ page: 1, items_per_page: 10 });
      setActions(res.data?.success ? res.data.data?.items ?? [] : []);
    } catch (err) {
      setActions(isNotDeployed(err) ? fakeAppleTransferActions().items : []);
    }
  };

  const refresh = () => {
    fetchSummary();
    fetchUsers();
    fetchActions();
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, status, team, hasSub, onlyErrors]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, status, team, hasSub, onlyErrors]);

  useEffect(() => {
    fetchSummary();
    fetchActions();
  }, []);

  // Follow a queued/running batch until it ends, then reload everything.
  const runningId = isActive(runningAction) ? runningAction.id : null;
  useEffect(() => {
    if (!runningId) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await fetchAppleTransferAction(runningId);
        const log = res.data?.data?.action_log;
        if (isActive(log)) {
          setRunningAction(log);
        } else {
          setRunningAction(null);
          setNotice(`Run #${runningId} ${log?.status ?? 'ended'}: ${log?.result ?? ''}`);
          refresh();
        }
      } catch (err) {
        setRunningAction(null);
        setError(describeError(err, 'Failed to follow the run'));
      }
    }, POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningId]);

  const hasActiveFilters = searchTerm.trim() || status || team || hasSub || onlyErrors;

  const clearFilters = () => {
    setSearchTerm('');
    setStatus('');
    setTeam('');
    setHasSub('');
    setOnlyErrors(false);
  };

  const closeConfirm = () => {
    setConfirmUser(null);
    setConfirmRun(null);
    setConfirmError(null);
  };

  const openRun = async (button) => {
    setConfirmError(null);
    setConfirmRun({ ...button, loading: true });
    try {
      const data = fake
        ? fakeAppleTransferPreview(button.scope, button.action)
        : (await previewAppleTransferBatch(button.scope, button.action)).data?.data;
      setConfirmRun({ ...button, action: data?.action, total: data?.total ?? 0, loading: false });
    } catch (err) {
      setConfirmRun({ ...button, loading: false });
      setConfirmError(describeError(err, 'Failed to count users'));
    }
  };

  const submitUser = async () => {
    const res = await transferAppleUser(confirmUser.id, confirmUser.next_action);
    const { result } = res.data?.data ?? {};
    if (result?.ok) {
      setNotice(`User ${confirmUser.id}: ${result.status}`);
    } else {
      setError(`User ${confirmUser.id}: ${result?.status ?? 'error'}${result?.error ? ` (${result.error})` : ''}`);
    }
    closeConfirm();
    refresh();
  };

  const submitRun = async () => {
    try {
      // Run exactly the action the preview counted and the admin confirmed.
      const res = await runAppleTransferBatch(confirmRun.scope, confirmRun.total, confirmRun.action);
      const data = res.data?.data;
      setNotice(`Queued ${data?.queued ?? 0} users (${data?.action}, ${runDescription(confirmRun)}).`);
      setRunningAction(data?.action_log ?? null);
      closeConfirm();
      refresh();
    } catch (err) {
      const code = errorCode(err);
      if (code === 'total_changed') {
        // Keep the dialog open with the new number so the admin confirms again.
        setConfirmRun({ ...confirmRun, total: err.response.data.data?.total ?? 0, changed: true });
      } else if (code === 'batch_running') {
        setRunningAction(err.response.data.data?.running_action ?? null);
        closeConfirm();
        setError(describeError(err));
      } else {
        throw err;
      }
    }
  };

  const submitConfirm = async () => {
    setSubmitting(true);
    setConfirmError(null);
    setNotice(null);
    try {
      if (fake) {
        setNotice('Fake data: nothing was sent. The admin API is not on this server yet.');
        closeConfirm();
      } else if (confirmUser) {
        await submitUser();
      } else if (confirmRun) {
        await submitRun();
      }
    } catch (err) {
      setConfirmError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelRun = async () => {
    try {
      const res = await cancelAppleTransferAction(runningAction.id);
      setNotice(`Run #${runningAction.id} cancelled: ${res.data?.data?.action_log?.result ?? ''}`);
      setRunningAction(null);
      refresh();
    } catch (err) {
      setError(describeError(err, 'Failed to cancel the run'));
    }
  };

  const countTiles = [
    ['Apple users', counts.total],
    ['Team VI', counts.team_vi],
    ['Team HK', counts.team_hk],
    ['Has transfer_sub', counts.with_transfer_sub],
    ...STATUSES.map((s) => [s, counts[s]]),
  ];

  const runBusy = Boolean(runningId);
  const progress = runningAction?.total ? Math.round((runningAction.processed / runningAction.total) * 100) : 0;

  return (
    <div className="dashboard-list-page apple-transfer-page">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h2 className="mb-0">
          <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
          Apple Transfer
          {fake && <Badge bg="warning" text="dark" className="ms-2 fs-6">Fake data</Badge>}
          {summary?.phase && (
            <Badge
              bg={summary.phase === 'after_transfer' ? 'success' : 'secondary'}
              className={`ms-2 text-white apple-transfer-phase ${summary.phase === 'after_transfer' ? 'is-after' : 'is-before'}`}
            >
              {summary.phase === 'after_transfer' ? 'After transfer (HK key)' : 'Before transfer (VI key)'}
            </Badge>
          )}
        </h2>
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="outline-secondary" onClick={refresh} disabled={loading}>
            <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
            Refresh
          </Button>
          {runButtonsFor(summary?.phase).map((button) => (
            <Button
              key={button.key}
              variant={button.variant}
              className="apple-transfer-run"
              disabled={!canEdit || loading || runBusy}
              title={runBusy ? 'Another run is in progress' : button.description}
              onClick={() => openRun(button)}
            >
              <FontAwesomeIcon icon={faPlay} className="me-1" />
              Run now: {button.label}
            </Button>
          ))}
        </div>
      </div>
      <p className="text-muted mb-3">
        Sign in with Apple users moving from the VI team to Appixi HK. Before the transfer, Transfer fetches
        the <code>transfer_sub</code> with the VI key; after it, Transfer exchanges it for the HK sub. Batches run in the queue.
      </p>

      {fake && (
        <Alert variant="warning" className="mb-3">
          The admin API (<code>/songs/admin/apple-transfer</code>) is not on this server yet. The list
          below is made-up data and the buttons send nothing.
        </Alert>
      )}
      {summary?.migration_hold && (
        <Alert variant="info" className="mb-3">
          Migration hold is on: unknown Apple subs get "try again later" instead of a new account.
        </Alert>
      )}
      {runBusy && (
        <Alert variant="primary" className="mb-3 apple-transfer-running">
          <div className="apple-transfer-running__body">
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
              <span>
                Run #{runningAction.id} ({runningAction.action}, {runningAction.target}) {runningAction.status}:{' '}
                {runningAction.result}
              </span>
              {canEdit && (
                <Button size="sm" variant="outline-danger" onClick={cancelRun}>
                  <FontAwesomeIcon icon={faStop} className="me-1" />
                  Cancel
                </Button>
              )}
            </div>
            <div className="apple-transfer-progress">
              <ProgressBar
                now={progress}
                aria-label={`Run #${runningAction.id} progress`}
                aria-valuetext={`${runningAction.processed ?? 0} of ${runningAction.total ?? 0}`}
              />
              <span className="apple-transfer-progress__label">
                {runningAction.processed ?? 0} / {runningAction.total ?? 0} ({progress}%)
              </span>
            </div>
          </div>
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" className="mb-3" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Row className="g-2 mb-3">
        {countTiles.map(([label, value]) => (
          <Col key={label} xs={6} md={4} lg>
            <Card className="h-100">
              <Card.Body className="py-2">
                <div className="text-muted small text-capitalize">{label}</div>
                <div className="fs-4 fw-bold">{value ?? '—'}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col xs={12} md={4}>
              <Form.Label className="small mb-1">
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                User ID or email
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="8421 or name@privaterelay.appleid.com"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Status</Form.Label>
              <Form.Select className="form-control form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Team</Form.Label>
              <Form.Select className="form-control form-select" value={team} onChange={(e) => setTeam(e.target.value)}>
                <option value="">All</option>
                <option value="vi">VI</option>
                <option value="hk">HK</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">transfer_sub</Form.Label>
              <Form.Select className="form-control form-select" value={hasSub} onChange={(e) => setHasSub(e.target.value)}>
                <option value="">All</option>
                <option value="1">Has it</option>
                <option value="0">Missing</option>
              </Form.Select>
            </Col>
            <Col xs={6} md="auto">
              <Form.Check
                type="switch"
                id="apple-transfer-only-errors"
                label="Only errors"
                checked={onlyErrors}
                onChange={(e) => setOnlyErrors(e.target.checked)}
              />
            </Col>
            {hasActiveFilters && (
              <Col xs="auto">
                <Button variant="outline-secondary" onClick={clearFilters}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Clear filters
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={PER_PAGE}
            onPageChange={setCurrentPage}
            position="top"
          />

          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="table-vcenter mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Apple sub</th>
                    <th>Team</th>
                    <th>transfer_sub</th>
                    <th>Status</th>
                    <th>Last error</th>
                    <th>Last run</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No Apple users found
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>
                          <div>{row.email || '—'}</div>
                          <div className="text-muted small">{row.name || ''}</div>
                        </td>
                        <td><code className="small">{row.apple_id || '—'}</code></td>
                        <td>
                          <Badge
                            bg={row.apple_sub_team === 'hk' ? 'success' : 'secondary'}
                            className={`text-white apple-transfer-team ${row.apple_sub_team === 'hk' ? 'is-hk' : 'is-vi'}`}
                          >
                            {(row.apple_sub_team || '—').toUpperCase()}
                          </Badge>
                        </td>
                        <td>
                          {row.apple_transfer_sub ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`transfer-sub-${row.id}`}>{row.apple_transfer_sub}</Tooltip>}
                            >
                              <code className="small apple-transfer-sub" tabIndex={0}>{shortSub(row.apple_transfer_sub)}</code>
                            </OverlayTrigger>
                          ) : row.has_transfer_sub ? (
                            <Badge bg="info" className="text-white">yes</Badge>
                          ) : (
                            <span className="text-muted">no</span>
                          )}
                        </td>
                        <td>
                          <Badge
                            bg={STATUS_VARIANTS[row.apple_migration_status] || 'secondary'}
                            className={row.apple_migration_status === 'conflict' ? 'text-dark' : 'text-white'}
                          >
                            {row.apple_migration_status || '—'}
                          </Badge>
                        </td>
                        <td className="small text-danger">{row.apple_migration_error || <span className="text-muted">—</span>}</td>
                        <td className="small">{formatDate(row.apple_migration_at)}</td>
                        <td className="text-end text-nowrap">
                          <Button
                            variant="outline-secondary"
                            className="me-2"
                            onClick={() => setDetailUser(row)}
                          >
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                            Detail
                          </Button>
                          <Button
                            variant="outline-primary"
                            disabled={!canEdit || !row.next_action}
                            title={row.next_action ? undefined : 'Nothing to do for this user'}
                            onClick={() => setConfirmUser(row)}
                          >
                            <FontAwesomeIcon icon={faExchangeAlt} className="me-1" />
                            {NEXT_ACTION_LABELS[row.next_action] || 'Transfer'}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={PER_PAGE}
            onPageChange={setCurrentPage}
            position="bottom"
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title className="mb-0">Recent admin actions</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {actions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">No actions yet</td>
                  </tr>
                ) : (
                  actions.map((a) => (
                    <tr key={a.id}>
                      <td className="small">{formatDate(a.created_at)}</td>
                      <td>{a.admin_email || '—'}</td>
                      <td>{a.action}</td>
                      <td>{a.target}</td>
                      <td>{a.status || '—'}</td>
                      <td>{a.result}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal show={Boolean(confirmUser || confirmRun)} onHide={closeConfirm} centered className="apple-transfer-modal">
        <Modal.Header closeButton>
          <Modal.Title>{confirmUser ? 'Transfer this user?' : 'Run the batch now?'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmError && <Alert variant="danger">{confirmError}</Alert>}
          {confirmUser && (
            <>
              <p className="mb-2">
                User <strong>{confirmUser.id}</strong> ({confirmUser.email}), status{' '}
                <strong>{confirmUser.apple_migration_status}</strong>.
              </p>
              <p className="mb-0 text-muted small">
                This calls Apple right away: <strong>{NEXT_ACTION_LABELS[confirmUser.next_action]}</strong>.
              </p>
            </>
          )}
          {confirmRun && (confirmRun.loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Counting users...
            </div>
          ) : confirmRun.total !== undefined && (
            <>
              {confirmRun.changed && (
                <Alert variant="warning">The number of users changed; please confirm again.</Alert>
              )}
              <p className="mb-2">
                <strong>{confirmRun.total}</strong> users ({runDescription(confirmRun)}) will be queued
                for <strong>{confirmRun.action}</strong>.
              </p>
              {confirmRun.key === GENERATE_BUTTON.key && (
                <p className="mb-2 small">
                  Each user gets a <code>transfer_sub</code> from the VI key. Users that already have one are
                  not touched; run &quot;users still pending&quot; afterwards to exchange the new ones for the HK sub.
                </p>
              )}
              <p className="mb-0 text-muted small">
                The job calls Apple at about 5 users per second; progress shows above while it runs.
              </p>
            </>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeConfirm} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={confirmRun?.variant === 'danger' ? 'danger' : 'primary'}
            onClick={submitConfirm}
            disabled={submitting || confirmRun?.loading || (confirmRun && !confirmRun.total)}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      <AppleTransferUserDetail row={detailUser} fake={fake} onHide={() => setDetailUser(null)} />
    </div>
  );
};

export default AppleTransfer;
